import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase, MapPin, Clock, DollarSign, ArrowRight,
  Shield, Code, TrendingUp, Users, CheckCircle, Star
} from 'lucide-react';
import SEO from '../components/SEO';

const jobs = [
  {
    id: 'security-engineer',
    title: 'Security Engineer',
    department: 'Engineering',
    type: 'Full-Time',
    location: 'India (Hybrid)',
    experience: '6-7 years',
    ctc: '5 Lacs CTC + Bonus',
    icon: Code,
    color: 'from-blue-500 to-cyan-500',
    description: 'Join our core engineering team to build and scale SecureSphere — the most comprehensive enterprise security platform. You will work on threat detection engines, AI/ML security models, and carrier-grade infrastructure.',
    responsibilities: [
      'Design and implement security detection engines for IoT, telecom, and AI workloads',
      'Build and optimize real-time threat analysis pipelines processing 10B+ events/day',
      'Develop AI-powered prompt injection detection and agentic AI guardrails',
      'Architect scalable microservices with FastAPI, React, MongoDB, and WebSocket',
      'Collaborate with product and research teams on zero-day detection capabilities',
      'Contribute to open-source security tools and publish security research'
    ],
    requirements: [
      '6-7 years of professional software engineering experience',
      'Strong proficiency in Python, JavaScript/TypeScript, and React',
      'Experience with cybersecurity — network security, threat detection, or SIEM',
      'Familiarity with AI/ML frameworks (PyTorch, TensorFlow) is a strong plus',
      'Experience with distributed systems, message queues, and real-time processing',
      'Knowledge of telecom protocols (SS7, Diameter, GTP) is a bonus'
    ]
  },
  {
    id: 'sales-security',
    title: 'Sales Executive — Security Solutions',
    department: 'Sales',
    type: 'Full-Time',
    location: 'India (Travel Required)',
    experience: '7-8 years',
    ctc: '7 Lacs CTC + Commission',
    icon: TrendingUp,
    color: 'from-orange-500 to-amber-500',
    description: 'Drive revenue growth by selling SecureSphere to enterprise customers and telecom carriers. You will own the full sales cycle from prospecting to closing, working with CISOs, CTOs, and security leaders across industries.',
    responsibilities: [
      'Identify and qualify enterprise and carrier prospects for SecureSphere',
      'Conduct product demonstrations and technical presentations to CISOs and security teams',
      'Manage full sales cycle: prospecting, discovery, proposal, negotiation, close',
      'Build and maintain relationships with key decision-makers in target accounts',
      'Collaborate with pre-sales engineering for custom solution design',
      'Achieve and exceed quarterly and annual revenue targets'
    ],
    requirements: [
      '7-8 years of B2B sales experience in cybersecurity, SaaS, or enterprise software',
      'Proven track record of selling security solutions to enterprise customers',
      'Strong understanding of cybersecurity landscape — SIEM, EDR, NDR, XDR, or similar',
      'Experience selling to telecom operators or large enterprises is a strong plus',
      'Excellent presentation, negotiation, and communication skills',
      'Willingness to travel for customer meetings and industry events'
    ]
  }
];

const CareersPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      <SEO title="Careers at SecureSphere | Infuse.AI — Join Our Team" description="Join the SecureSphere team at Infuse.AI. We're hiring security engineers and sales executives to help secure the connected world." keywords="careers, jobs, security engineer, sales executive, cybersecurity jobs, Infuse.AI careers" />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 hover:opacity-80 transition">
            <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">™</span></span>
          </button>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/about')} className="text-sm text-neutral-300 hover:text-white transition hidden md:block">About</button>
            <button onClick={() => navigate('/login')} className="px-5 py-2.5 bg-[#F26419] text-white font-semibold rounded-lg text-sm hover:bg-[#F7B731] hover:text-black transition-all">Sign In</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2a1f1a] to-[#1a1a1a]" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-sm font-medium mb-6">
              <Briefcase className="w-4 h-4 inline mr-1" /> Careers at SecureSphere
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">Build the Future of<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F7B731] to-[#F26419]">Cybersecurity</span></h1>
            <p className="text-lg text-neutral-400 max-w-3xl mx-auto">Join a team of passionate security engineers, researchers, and business leaders who are building the world's most comprehensive enterprise security platform.</p>
          </motion.div>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-16 bg-[#242424]/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center mb-10">Why Join SecureSphere?</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, label: "Mission-Driven", desc: "Protect millions of devices and subscribers worldwide" },
              { icon: Star, label: "Cutting-Edge Tech", desc: "AI/ML, real-time systems, carrier-grade scale" },
              { icon: Users, label: "World-Class Team", desc: "75+ engineers across 12 countries" },
              { icon: TrendingUp, label: "Growth", desc: "Fast-growing company with massive market opportunity" }
            ].map((item, i) => (
              <motion.div key={item.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center p-5">
                <item.icon className="w-7 h-7 text-orange-400 mx-auto mb-3" />
                <h4 className="text-white font-semibold mb-1 text-sm">{item.label}</h4>
                <p className="text-xs text-neutral-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Open Positions</h2>
          <p className="text-neutral-400 text-center mb-12">We're looking for exceptional talent to join our mission.</p>

          <div className="space-y-8">
            {jobs.map((job, i) => (
              <motion.div key={job.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="bg-[#242424] border border-white/10 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all">
                {/* Job Header */}
                <div className={`bg-gradient-to-r ${job.color} p-6`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-black/20 flex items-center justify-center">
                        <job.icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{job.title}</h3>
                        <p className="text-white/80 text-sm">{job.department}</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-black/20 rounded-full text-white text-xs font-medium">{job.type}</span>
                  </div>
                </div>

                {/* Job Details */}
                <div className="p-6 space-y-6">
                  {/* Meta */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2 text-sm text-neutral-400">
                      <MapPin className="w-4 h-4" /> {job.location}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-neutral-400">
                      <Clock className="w-4 h-4" /> {job.experience} experience
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#F7B731] font-semibold">
                      <DollarSign className="w-4 h-4" /> {job.ctc}
                    </div>
                  </div>

                  <p className="text-neutral-300 leading-relaxed">{job.description}</p>

                  {/* Responsibilities */}
                  <div>
                    <h4 className="text-white font-semibold mb-3">What You'll Do</h4>
                    <div className="grid md:grid-cols-2 gap-2">
                      {job.responsibilities.map((r, j) => (
                        <div key={`r-${j}`} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-neutral-400">{r}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Requirements */}
                  <div>
                    <h4 className="text-white font-semibold mb-3">What We're Looking For</h4>
                    <div className="grid md:grid-cols-2 gap-2">
                      {job.requirements.map((r, j) => (
                        <div key={`r-${j}`} className="flex items-start gap-2">
                          <ArrowRight className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-neutral-400">{r}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Apply CTA */}
                  <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                    <p className="text-xs text-neutral-500">Send your resume to apply</p>
                    <a href={`mailto:info@infuse.net.in?subject=Application: ${job.title}&body=Hi, I'm interested in the ${job.title} position at SecureSphere.`} className="px-6 py-3 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-full text-sm hover:shadow-[0_0_30px_rgba(242,100,25,0.4)] transition-all flex items-center gap-2">
                      Apply Now <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* General CTA */}
      <section className="py-16 bg-gradient-to-r from-[#F26419]/10 to-[#F7B731]/10 border-y border-orange-500/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold mb-3">Don't See Your Role?</h2>
          <p className="text-neutral-400 mb-6">We're always looking for exceptional talent. Send us your resume and tell us how you'd like to contribute.</p>
          <a href="mailto:info@infuse.net.in?subject=General Application — SecureSphere&body=Hi, I'm interested in joining SecureSphere." className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/20 text-white font-medium rounded-full text-lg hover:bg-white/10 transition-all">
            Send Your Resume <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#242424] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-neutral-500">&copy; 2026 Infuse Technologies. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-sm text-neutral-500 hover:text-white transition">Home</button>
            <button onClick={() => navigate('/about')} className="text-sm text-neutral-500 hover:text-white transition">About</button>
            <a href="mailto:info@infuse.net.in" className="text-sm text-neutral-500 hover:text-white transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;
